// Conversation list rail.
//
// Owns the filter chip bar + URL-synced filter state + the row list. Selecting
// a row navigates to /conversations/:id; the rail stays mounted.

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listConversations } from "./api";
import { useFiltersFromUrl } from "./useFiltersFromUrl";
import type {
  ConversationKind,
  ConversationSummary,
} from "./types";

const KIND_LABEL: Record<ConversationKind, string> = {
  meeting: "Meeting",
  slack_channel: "Slack",
  slack_dm: "Slack DM",
  slack_group_dm: "Slack",
  email_thread: "Email",
  calendar_event: "Calendar",
};

const ALL_KINDS: ConversationKind[] = [
  "meeting",
  "slack_channel",
  "slack_dm",
  "slack_group_dm",
  "email_thread",
  "calendar_event",
];

const DATE_PRESETS: Array<{ token: string; label: string }> = [
  { token: "last-7d", label: "7d" },
  { token: "last-30d", label: "30d" },
  { token: "last-90d", label: "90d" },
];

function formatTs(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function previewParticipants(row: ConversationSummary): string {
  const names = row.participants
    .map((p) => p.display_name || p.email || p.slack_user_id || "")
    .filter(Boolean)
    .slice(0, 4);
  return names.join(", ") + (row.participants.length > 4 ? ` +${row.participants.length - 4}` : "");
}

interface Props {
  selectedId: string | null;
}

export function ConversationListPage({ selectedId }: Props): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const { filters, setFilter } = useFiltersFromUrl(params, setParams);

  const queryKey = useMemo(() => ["conversations", JSON.stringify(filters)], [filters]);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => listConversations(filters, 50, 0),
  });

  function toggleKind(k: ConversationKind) {
    if (filters.kinds.includes(k)) {
      setFilter(
        "kinds",
        filters.kinds.filter((x) => x !== k),
      );
    } else {
      setFilter("kinds", [...filters.kinds, k]);
    }
  }

  function setDatePreset(token: string) {
    setFilter("since", filters.since === token ? null : token);
  }

  return (
    <>
      <div className="cv-rail-header">
        <input
          className="cv-search-input"
          placeholder="Search conversations"
          value={filters.q ?? ""}
          onChange={(e) => setFilter("q", e.target.value || null)}
        />
        {ALL_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={`cv-filter-chip${filters.kinds.includes(k) ? " active" : ""}`}
            onClick={() => toggleKind(k)}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
        {DATE_PRESETS.map((p) => (
          <button
            key={p.token}
            type="button"
            className={`cv-filter-chip${filters.since === p.token ? " active" : ""}`}
            onClick={() => setDatePreset(p.token)}
          >
            {p.label}
          </button>
        ))}
        {filters.participants.length > 0 && (
          <span className="cv-filter-chip-mute">
            with {filters.participants.join(", ")}
          </span>
        )}
      </div>

      {isLoading && <div className="cv-loading">Loading…</div>}
      {error && (
        <div className="cv-error">{(error as Error).message}</div>
      )}
      {data && data.items.length === 0 && (
        <div className="cv-loading">No conversations match these filters.</div>
      )}
      {data?.items.map((row) => (
        <Link
          key={row.id}
          to={`/conversations/${row.id}${params.toString() ? `?${params}` : ""}`}
          className={`cv-row${row.id === selectedId ? " selected" : ""}`}
        >
          <div className="cv-row-kind">{KIND_LABEL[row.kind]}</div>
          <div className="cv-row-body">
            <div className="cv-row-title">{row.title || "(untitled)"}</div>
            <div className="cv-row-meta">
              {previewParticipants(row)}
              {row.turn_count > 0 && ` · ${row.turn_count} turns`}
            </div>
          </div>
          <div className="cv-row-ts">{formatTs(row.started_at)}</div>
        </Link>
      ))}
    </>
  );
}
